import React, { useState } from 'react';
import { Box, Button, Typography, Container, AppBar, Toolbar, Link, GlobalStyles } from '@mui/material';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import RotatingText from './RotatingText';
import { useNavigate } from "react-router-dom";

// --- Your existing file paths ---
const mapImageUrl = '/icons/landing-page-map-2.svg'; 
const logoImageUrl = '/icons/landing-page-logo.svg';
const icon1Url = '/icons/optimise-cloud-costs.svg';
const icon2Url = '/icons/troubleshoot-cloud-issues.svg';
const icon3Url = '/icons/strengthen-cloud-security-posture.svg';
const tickIconUrl = '/icons/tick-green.svg';
const crossIconUrl = '/icons/cross-red.svg';
const footerBgUrl = '/icons/landing-page-footer-bg-1.svg';

// --- Placeholders for the THREE new background images ---
const heroBgLeftUrl = '/icons/landing-page-bg-hero.svg';
const howItHelpsBgRightUrl = '/icons/how-it-helps-bg-1.svg';
const whyChooseUsBgUrl = '/icons/why-choose-us-bg.svg';

// --- Placeholders for the NEW Key Features section images ---
const feature1BgUrl = '/icons/feature-card-bg-1.svg';
const feature1ImageUrl = '/icons/feature-card-image-1.svg';
const feature2BgUrl = '/icons/feature-card-bg-2.svg';
const feature2ImageUrl = '/icons/feature_2.svg';
const feature2Image1Url= '/icons/feature-card-image-2-1.svg';
const feature3BgUrl = '/icons/feature-card-bg-3.svg';
const feature3ImageUrl = '/icons/feature-card-image-3-1.svg';
const feature4BgUrl = '/icons/feature-card-bg-4.svg';
const feature4ImageUrl = '/icons/feature_4.svg';
const feature5ImageUrl = '/icons/feature_5.svg';


// --- Data for the new section ---
const withViewData = [
  "See and manage all your GCP, AWS, Azure, and Kubernetes resources in one place.",
  "Gain actionable security insights and recommendations to fortify your cloud defenses.",
  "Quickly identify and resolve issues with an intelligent copilot, reducing downtime.",
  "Implement policies to automatically manage and reduce non-production workload expenses.",
];

const withoutViewData = [
  "Siloed insights across different cloud providers, leading to incomplete pictures.",
  "Unaware of potential vulnerabilities and misconfigurations, increasing breach exposure.",
  "Lengthy, complex, and time-consuming troubleshooting processes across disparate systems.",
  "Development and staging environments easily consuming unplanned budget.",
];

// --- Data for the new Key Features section ---
// This data is now used for reference, as the cards are hardcoded below.
const keyFeaturesData = [
    {
        number: '01',
        title: 'Cost Overview & Analysis Dashboard',
        description: 'Track real-time spend by tenant, project, or data source, and never lose sight of where your money goes.',
        bgColor: '#0064FF',
        bgUrl: feature1BgUrl,
        imageUrl: feature1ImageUrl,
    },
    {
        number: '02',
        title: 'Intelligent Troubleshooting Copilot',
        description: 'Diagnose and resolve cloud incidents faster than ever. Get AI-powered suggestions and guided workflows to minimize downtime.',
        bgColor: '#0d244f',
        bgUrl: feature2BgUrl,
        imageUrl: feature2ImageUrl,
    },
    {
        number: '03',
        title: 'Proactive Security Posture Management',
        description: 'Continuously scan for misconfigurations and vulnerabilities across all your clouds. Get prioritized alerts and remediation steps.',
        bgColor: '#1a1a1d',
        bgUrl: feature3BgUrl,
        imageUrl: feature3ImageUrl,
    },
    {
        number: '04',
        title: 'Automated Non-Production Governance',
        description: 'Implement policies to automatically shut down idle dev/staging resources. Prevent budget waste without slowing down your teams.',
        bgColor: '#4a4e69',
        bgUrl: feature4BgUrl,
        imageUrl: feature4ImageUrl,
    },
];


// =======================================================================
// UPDATED ANIMATION VARIANTS
// =======================================================================

// Animation variant for text coming from the left
const fromLeftVariant: Variants = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { duration: 1, ease: 'easeOut' } }
};

// Animation variant for buttons coming from the top
const fromTopVariant: Variants = {
  hidden: { opacity: 0, y: -40 },
  visible: { opacity: 1, y: 0, transition: { duration: 1, ease: 'easeOut' } }
};

// Animation variant for content coming from the right
const fromRightVariant: Variants = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 1, ease: 'easeOut' } }
};

// Reusable animation variants for sections coming from the bottom
const fromBottomVariant: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1,
      ease: 'easeOut'
    }
  }
};

// Animation variants for a container and its staggered items
const containerStaggerAnimation: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};


const LandingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('with');
  const navigate = useNavigate();

  return (
    <>
      <GlobalStyles styles={{ html: { scrollBehavior: 'smooth' } }} />
      
      {/* ======================================================================= */}
      {/* HERO SECTION (ANIMATION CHANGED)                                        */}
      {/* ======================================================================= */}
      <Box sx={{ 
          width: '100%', 
          minHeight: '100vh', 
          backgroundColor: '#fff',
          position: 'relative',
          overflow: 'hidden',
          px:5 
      }}>
        {/* Background Image: Stuck to the left */}
        <Box
          component="img"
          src={heroBgLeftUrl}
          alt="decorative background left"
          sx={{
            position: 'absolute',
            left: 0,
            bottom: 50,
            width: { xs: '100%', md: '100%' },
            height: 'auto',
            zIndex: 0,
            pointerEvents: 'none',
            opacity:1
          }}
        />
        <Box
          component="img"
          src={mapImageUrl}
          alt="World map with data points"
          sx={{
            position: 'absolute',
            top: '80px',
            right: 0,
            width: { xs: '100%', md: '65%' }, 
            height: 'auto',
            opacity: { xs: 0.3, md: 0.8 }, 
            zIndex: 0,
          }}
        />
        <Container maxWidth={false} sx={{ position: 'relative', zIndex: 2 }}>
          <AppBar position="static" color="transparent" elevation={0} sx={{ pt: 2 }}>
            <Toolbar>
              <Box sx={{ 
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                  <Box 
                      component="img"
                      src={logoImageUrl}
                      alt="Horizon Logo"
                      sx={{ height: '35px' }}
                  />
                  <Typography
                      variant="h5"
                      component="div"
                      sx={{
                          color: '#0064FF', 
                          fontWeight: 900, 
                          fontFamily: 'Gilroy-SemiBold',
                          fontSize: '22px', 
                          lineHeight: '15px', 
                          letterSpacing: '1px', 
                          whiteSpace: 'nowrap',
                      }}
                  >
                      Horizon
                  </Typography>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                <Link href="#how-it-helps" sx={{ fontFamily:'Manrope', fontSize: '14px', color: '#5F6B7A', mx: 2, textDecoration: 'none', fontWeight: 400, '&:hover': { color: '#333333' } }}>How It Helps</Link>
                <Link href="#key-features" sx={{ fontFamily:'Manrope', fontSize: '14px', color: '#5F6B7A', mx: 2, textDecoration: 'none', fontWeight: 400, '&:hover': { color: '#333333' } }}>Key Features</Link>
                <Link href="#why-choose-us" sx={{ fontFamily:'Manrope', fontSize: '14px', color: '#5F6B7A', mx: 2, textDecoration: 'none', fontWeight: 400, '&:hover': { color: '#333333' } }}>Why Choose Us</Link>
                  <Button 
                      variant="outlined" 
                      sx={{ fontFamily:'Manrope', fontSize: '14px', width:'98px', fontWeight:500, borderColor: '#0064FF', color: '#0064FF', borderRadius: '12px', textTransform: 'none', ml: 2, '&:hover': { borderColor: '#357ae8', backgroundColor: 'rgba(66, 133, 244, 0.04)' } }}
                      onClick={() => navigate("/login")}
                  >
                      Login
                  </Button>
              </Box>
            </Toolbar>
          </AppBar>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', mt: { xs: 4, md: 12 } }}>
            <Box sx={{ width: { xs: '100%', md: '50%' }, pr: { md: 2 }, pl: { md: 3 } }}>
              <motion.div initial="hidden" animate="visible" variants={containerStaggerAnimation}>
                <Box sx={{ textAlign: { xs: 'center', md: 'left' }, maxWidth: '550px', mx: { xs: 'auto', md: 'initial' } }}>
                  {/* --- CHANGE: Text now comes from left --- */}
                  <motion.div variants={fromLeftVariant}>
                    <Typography sx={{ fontFamily:'Manrope', fontSize: '18px', color: '#0064FF', fontWeight: '600', mb: 1 }}>
                      WELCOME TO HORIZON
                    </Typography>
                  </motion.div>
                  {/* --- CHANGE: Text now comes from left --- */}
                  <motion.div variants={fromLeftVariant}>
                    <Typography  sx={{ fontFamily: 'Manrope', fontSize: '80px', fontWeight: 500, color: '#333333', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                      All-in-one <br/>platform that does{' '}<br/>
                      <Typography component="span" variant="inherit" sx={{lineHeight: 1.3}}>
                        <RotatingText />
                      </Typography>
                    </Typography>
                  </motion.div>
                  {/* --- CHANGE: Text now comes from left --- */}
                  <motion.div variants={fromLeftVariant}>
                    <Typography  sx={{ fontFamily:'Manrope', fontSize: '16px', color: '#666666', mt: 3, mb: 4, fontWeight: 400 }}>
                      Unify your cloud operations for smarter spending, faster fixes, and stronger protection across all your providers.
                    </Typography>
                  </motion.div>
                  {/* --- CHANGE: Button now comes from top --- */}
                  <motion.div variants={fromTopVariant}>
                    <Button 
                      variant="contained" 
                      size="large" 
                      sx={{ backgroundColor: '#0064FF', borderRadius: '12px', width:'170px', py: 1.5, px: 4, textTransform: 'none', fontSize: '16px', fontFamily:'Manrope', fontWeight: 600, '&:hover': { backgroundColor: '#357ae8' } }}
                      onClick={() => navigate("/login")}
                    >
                      Try Yourself
                    </Button>
                  </motion.div>
                </Box>
              </motion.div>
            </Box>
            <Box sx={{ width: { xs: '100%', md: '50%' } }} />
          </Box>
        </Container>
      </Box>

      {/* ======================================================================= */}
      {/* HOW IT HELPS SECTION (ANIMATION CHANGED)                                */}
      {/* ======================================================================= */}
      <Box sx={{ position: 'relative', py: 10, backgroundColor: '#fff', overflow: 'hidden', px:5, }}>
          <Container maxWidth={false} sx={{position: 'relative', zIndex: 1}}>
              <Box id="how-it-helps" sx={{ pr: { md: 2 }, pl: { md: 3 }, scrollMarginTop: 100 }}>
                  {/* --- CHANGE: Content now comes from bottom --- */}
                  <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fromBottomVariant}>
                    <Typography sx={{ fontFamily: 'Manrope', color: '#0064FF', fontWeight: 600, mb: 2, fontSize:'18px' }}>
                        HOW IT HELPS
                    </Typography>
                    <Typography sx={{ fontFamily: 'Manrope', fontWeight: 500, color: '#999999', mb: 3, fontSize:'44px' }}>
                        Empower your team to <br />
                        <Typography component="span" variant="inherit" sx={{color: "#333333"}}>master cloud operations</Typography> with <Typography component="span" variant="inherit" sx={{color: "#333333"}}>precision</Typography> and <Typography component="span" variant="inherit" sx={{color: "#333333"}}>ease.</Typography>
                    </Typography>
                  </motion.div>
                  <Box sx={{ height: '1px', background: 'linear-gradient(90deg, #0064FF 0%, rgba(0, 100, 255, 0) 100%)', my: 6 }}/>
                  
                  {/* --- CHANGE: Staggered items now come from bottom --- */}
                  <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={containerStaggerAnimation} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                      <motion.div variants={fromBottomVariant}>
                        <Box sx={{ width: { xs: '100%', md: '25vw' }, mb: { xs: 5, md: 0 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '60px', height: '58px', backgroundColor: '#fff', borderRadius: '50%', boxShadow: '#98989826 0px 1px 2px', mb: 2, }}>
                                <Box component="img" src={icon1Url} alt="Multi-tenant cost visibility icon" sx={{  width: '30px', height: '29px' }} />
                            </Box>
                            <Typography sx={{ fontFamily: 'Manrope', fontSize:'20px', fontWeight: 600, mb: 1, color:'#333333'}}>Optimize cloud costs</Typography>
                            <Typography sx={{ fontFamily: 'Manrope', color: '#666666', fontSize:'16px', fontWeight:400 }}>Proactively optimize multi-cloud and Kubernetes spend with actionable FinOps, managing budgets precisely.</Typography>
                        </Box>
                      </motion.div>
                      <motion.div variants={fromBottomVariant}>
                        <Box sx={{ width: { xs: '100%', md: '25vw' }, mb: { xs: 5, md: 0 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '60px', height: '58px', backgroundColor: '#fff', borderRadius: '50%', boxShadow: '#98989826 0px 1px 2px', mb: 2, }}>
                                <Box component="img" src={icon2Url} alt="Create budgets & alerts icon" sx={{ width: '30px', height: '29px' }} />
                            </Box>
                            <Typography sx={{ fontFamily: 'Manrope', fontSize:'20px', fontWeight: 600, mb: 1, color:'#333333' }}>Troubleshoot cloud issues</Typography>
                            <Typography sx={{ fontFamily: 'Manrope', color: '#666666', fontSize:'16px', fontWeight:400}}>Rapidly resolve cloud incidents; your intelligent copilot aids quick diagnostics, minimizing downtime and impact.</Typography>
                        </Box>
                      </motion.div>
                      <motion.div variants={fromBottomVariant}>
                        <Box sx={{ width: { xs: '100%', md: '25vw' }, mb: { xs: 5, md: 0 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '60px', height: '58px', backgroundColor: '#fff', borderRadius: '50%', boxShadow: '#98989826 0px 1px 2px', mb: 2, }}>
                                <Box component="img" src={icon3Url} alt="Analyse Kubernetes icon" sx={{ width: '30px', height: '29px' }} />
                            </Box>
                            <Typography  sx={{ fontFamily: 'Manrope', fontSize:'20px', fontWeight: 600, mb: 1, color:'#333333' }}>Strengthen Cloud Security Posture</Typography>
                            <Typography sx={{ fontFamily: 'Manrope', color: '#666666', fontSize:'16px', fontWeight:400}}>Identify vulnerabilities, misconfigurations, and receive actionable recommendations to enhance defense and compliance.</Typography>
                        </Box>
                      </motion.div>
                  </motion.div>
              </Box>
          </Container>
      </Box>

      {/* =======================================================================
      {/* NEW KEY FEATURES SECTION                                                */}
      {/* ======================================================================= */}
      {/* {false && ( */}
        <Box id="key-features" sx={{ backgroundColor: '#FFFFFF', py: 10, scrollMarginTop: 60 }}>
        <Container maxWidth={false} sx={{position: 'relative', zIndex: 1}}>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fromBottomVariant}>
                <Typography sx={{ fontFamily: 'Manrope',color: '#0064FF', fontWeight: 600, mb: 2, fontSize:'18px', pl: { xs: 5, md: 8 }}}>
                    KEY FEATURES
                </Typography>
                <Typography sx={{ fontFamily: 'Manrope', fontWeight: 500, color: '#999999', mb: 3, fontSize:'44px',  pl: { xs: 5, md: 8 }}}>
                    Everything you need to <br />
                    <Typography component="span" variant="inherit" sx={{color: "#333333"}}>track, optimise,</Typography> and <Typography component="span" variant="inherit" sx={{color: "#333333"}}>control </Typography>cloud costs
                </Typography>
            </motion.div>
        </Container>

        {/* Stacking Cards Container - REFACTORED TO NOT USE .map() */}
        <Box sx={{ position: 'relative', mt: 8 }}>
            {/* --- CARD 1 --- */}
            <Box
                key="feature-1"
                sx={{
                    position: 'sticky', top: '0px', minHeight: '500px', backgroundColor: '#0064FF',
                    color: 'white', display: 'flex', alignItems: 'center', overflow: 'hidden', zIndex: 1,
                }}
            >
                <Container maxWidth={false} sx={{ position: 'relative', zIndex: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ width: { xs: '100%', md: '45%' }, p: { xs: 2, md: 4 }, pl: { xs: 5, md: 8 } }}>
                            <Typography sx={{ fontFamily: 'Manrope', fontSize: '22px', fontWeight: 700, color:'#FFFFFF', mb: 2 }}>
                                01
                            </Typography>
                            <Box sx={{ height: '1px', background: 'linear-gradient(90deg, #FFFFFF 0%, rgba(0, 100, 255, 0) 100%)', my: 3 }}/>
                            <Typography sx={{ fontFamily: 'Manrope', fontSize: { xs: '22px', md: '30px' }, color:'#FFFFFF', fontWeight: 600,  mb: 3, }}>
                                Cost Overview & Analysis Dashboard
                            </Typography>
                            
                            {/* <Typography sx={{ fontFamily: 'Manrope', fontSize: '16px', fontWeight: 400, color:'#FFFFFF', opacity: 1 }}>
                                Track real-time spend by tenant, project, or data source, and never lose sight of where your money goes.
                            </Typography> */}
                            <Typography sx={{ fontFamily: 'Manrope', fontSize: '16px', fontWeight: 400, color:'#FFFFFF', opacity: 1 }}>
                                Your complete cost picture, all in one place.
                            </Typography>
                        </Box>
                        <Box sx={{ width: { xs: '100%', md: '50%' }, display: 'flex', justifyContent: 'center', alignItems: 'center', p: { xs: 2, md: 4 } }}>
                            <Box component="img" src={feature1ImageUrl} alt="Cost Overview & Analysis Dashboard"
                                sx={{ width: '100%', maxWidth: '600px', height: 'auto', borderRadius: '16px'}}
                            />
                        </Box>
                    </Box>
                </Container>
                {/* <Box component="img" src={feature1BgUrl} alt="decorative background"
                    sx={{ position: 'absolute', bottom: -40, left: -380, width:'50%',height: '50%', zIndex: 1, pointerEvents: 'none', opacity: 1 }}
                />  */}
            </Box>

            {/* --- CARD 2 --- */}
            <Box
                key="feature-2"
                sx={{
                    position: 'sticky', top: '0px', minHeight: '500px', backgroundColor: '#FFFFFF',
                    color: 'white', display: 'flex', alignItems: 'center', overflow: 'hidden', zIndex: 2,
                }}
            >
                <Container maxWidth={false} sx={{ position: 'relative', zIndex: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ width: { xs: '100%', md: '45%' }, p: { xs: 2, md: 4 }, pl: { xs: 5, md: 8 } }}>
                            <Typography sx={{ fontFamily: 'Manrope', fontSize: '22px', fontWeight: 700, color:'#333333', mb: 2 }}>
                                02
                            </Typography>
                            <Box sx={{ height: '1px', background: 'linear-gradient(90deg, #E6E6E6 0%, rgba(0, 100, 255, 0) 100%)', my: 3 }}/>
                            <Typography sx={{ fontFamily: 'Manrope', fontSize: { xs: '22px', md: '30px' }, color:'#333333', fontWeight: 600,  mb: 3, }}>
                                Kubernetes Cost Analysis
                            </Typography>
                            
                            <Typography sx={{ fontFamily: 'Manrope', fontSize: '16px', fontWeight: 400, color:'#666666', opacity: 1 }}>
                                 Demystify your Kubernetes spend, from the cluster down to the pod.
                            </Typography>
                        </Box>
                        <Box sx={{ width: { xs: '100%', md: '50%' }, display: 'flex', justifyContent: 'center', alignItems: 'center', p: { xs: 2, md: 4 } }}>
                            <Box component="img" src={feature2ImageUrl} alt="Kubernetes cost Analysis"
                                sx={{ minWidth: '920px', height: 'auto', borderRadius: '16px', mr:'9.5vw'}}
                            />
                            
                        </Box>
                    </Box>
                </Container>
                {/* <Box component="img" src={feature2BgUrl} alt="decorative background"
                    sx={{ position: 'absolute', bottom: 0, left: 0, width: { xs: '80%', md: '50%' }, height: 'auto', zIndex: 1, pointerEvents: 'none', opacity: 0.1 }}
                /> */}
            </Box>

            {/* --- CARD 3 --- */}
            <Box
                key="feature-3"
                sx={{
                    position: 'sticky', top: '0px', minHeight: '500px', backgroundColor: '#03A65A',
                    color: 'white', display: 'flex', alignItems: 'center', overflow: 'hidden', zIndex: 3,
                }}
            >
                <Container maxWidth={false} sx={{ position: 'relative', zIndex: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ width: { xs: '100%', md: '45%' }, p: { xs: 2, md: 4 }, pl: { xs: 5, md: 8 } }}>
                            <Typography sx={{ fontFamily: 'Manrope', fontSize: '22px', fontWeight: 700, color:'#FFFFFF', mb: 2 }}>
                                03
                            </Typography>
                            <Box sx={{ height: '1px', background: 'linear-gradient(90deg, #FFFFFF 0%, rgba(0, 100, 255, 0) 100%)', my: 3 }}/>
                            <Typography sx={{ fontFamily: 'Manrope', fontSize: { xs: '22px', md: '30px' }, color:'#FFFFFF', fontWeight: 600,  mb: 3, }}>
                                Budgets and Alerts
                            </Typography>
                            
                            <Typography sx={{ fontFamily: 'Manrope', fontSize: '16px', fontWeight: 400, color:'#FFFFFF', opacity: 1 }}>
                                Stay ahead of cost surprises with proactive budget alerts.
                            </Typography>
                        </Box>
                        <Box sx={{ width: { xs: '100%', md: '50%' }, display: 'flex', justifyContent: 'center', alignItems: 'center', p: { xs: 2, md: 4 } }}>
                            <Box component="img" src={feature3ImageUrl} alt="Budgets and Alerts"
                                sx={{ minWidth: '700px', height: 'auto', borderRadius: '16px', mr:'6vw'}}
                            />
                        </Box>
                    </Box>
                </Container>
                {/* <Box component="img" src={feature3BgUrl} alt="decorative background"
                    sx={{ position: 'absolute', bottom: 0, left: 0, width: { xs: '80%', md: '50%' }, height: 'auto', zIndex: 1, pointerEvents: 'none', opacity: 0.1 }}
                /> */}
            </Box>

            {/* --- CARD 4 --- */}
            <Box
                key="feature-4"
                sx={{
                    position: 'sticky', top: '0px', minHeight: '500px', backgroundColor: '#000000',
                    color: 'white', display: 'flex', alignItems: 'center', overflow: 'hidden', zIndex: 4,
                }}
            >
                <Container maxWidth={false} sx={{ position: 'relative', zIndex: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ width: { xs: '100%', md: '45%' }, p: { xs: 2, md: 4 }, pl: { xs: 5, md: 8 } }}>
                            <Typography sx={{ fontFamily: 'Manrope', fontSize: '22px', fontWeight: 700, color:'#FFFFFF', mb: 2 }}>
                                04
                            </Typography>
                            <Box sx={{ height: '1px', background: 'linear-gradient(90deg, #FFFFFF 0%, rgba(0, 100, 255, 0) 100%)', my: 3 }}/>
                            <Typography sx={{ fontFamily: 'Manrope', fontSize: { xs: '22px', md: '30px' }, color:'#FFFFFF', fontWeight: 600,  mb: 3, }}>
                                Copilot
                            </Typography>
                            
                            <Typography sx={{ fontFamily: 'Manrope', fontSize: '16px', fontWeight: 400, color:'#FFFFFF', opacity: 1 }}>
                                Get actionable, AI-driven insights to optimize costs effortlessly.
                            </Typography>
                        </Box>
                        <Box sx={{ width: { xs: '100%', md: '50%' }, display: 'flex', justifyContent: 'center', alignItems: 'center', p: { xs: 2, md: 4 } }}>
                            <Box component="img" src={feature4ImageUrl} alt="Cost Overview & Analysis Dashboard"
                                sx={{ minWidth: '890px', height: 'auto', borderRadius: '16px', mr:'-3.5vw'}}
                            />
                        </Box>
                    </Box>
                </Container>
                {/* <Box component="img" src={feature4BgUrl} alt="decorative background"
                    sx={{ position: 'absolute', bottom: 0, left: 0, width: { xs: '80%', md: '50%' }, height: 'auto', zIndex: 1, pointerEvents: 'none', opacity: 0.1 }}
                /> */}
            </Box>
            {/* --- CARD 5 --- */}
            <Box
                key="feature-5"
                sx={{
                    position: 'sticky', top: '0px', minHeight: '500px', backgroundColor: '#FFA600',
                    color: 'white', display: 'flex', alignItems: 'center', overflow: 'hidden', zIndex: 4,
                }}
            >
                <Container maxWidth={false} sx={{ position: 'relative', zIndex: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ width: { xs: '100%', md: '45%' }, p: { xs: 2, md: 4 }, pl: { xs: 5, md: 8 } }}>
                            <Typography sx={{ fontFamily: 'Manrope', fontSize: '22px', fontWeight: 700, color:'#333333', mb: 2 }}>
                                05
                            </Typography>
                            <Box sx={{ height: '1px', background: 'linear-gradient(90deg, #FFFFFF 0%, rgba(0, 100, 255, 0) 100%)', my: 3 }}/>
                            <Typography sx={{ fontFamily: 'Manrope', fontSize: { xs: '22px', md: '30px' }, color:'#333333', fontWeight: 600,  mb: 3, }}>
                                LLM Observability
                            </Typography>
                            
                            <Typography sx={{ fontFamily: 'Manrope', fontSize: '16px', fontWeight: 400, color:'#333333', opacity: 1 }}>
                                Unlock granular control over your LLM spend by analyzing every token, trace, and model.
                            </Typography>
                        </Box>
                        <Box sx={{ width: { xs: '100%', md: '50%' }, display: 'flex', justifyContent: 'center', alignItems: 'center', p: { xs: 1, md: 1 } }}>
                            <Box component="img" src={feature5ImageUrl} alt="LLM Observability"
                                sx={{ minWidth: '890px', height: 'auto', borderRadius: '16px', mr:'-3.5vw'}}
                            />
                        </Box>
                    </Box>
                </Container>
                {/* <Box component="img" src={feature4BgUrl} alt="decorative background"
                    sx={{ position: 'absolute', bottom: 0, left: 0, width: { xs: '80%', md: '50%' }, height: 'auto', zIndex: 1, pointerEvents: 'none', opacity: 0.1 }}
                /> */}
            </Box>
        </Box>
      </Box> 
{/* )} */}

      {/* ======================================================================= */}
      {/* WHY CHOOSE US SECTION (ANIMATION CHANGED)                               */}
      {/* ======================================================================= */}
      <Box id="why-choose-us" sx={{ py: 20, backgroundColor: '#FFFFFF', overflow: 'hidden', px:5, position: 'relative', scrollMarginTop: 60 }}>
        <Box
            component="img"
            src={whyChooseUsBgUrl}
            alt="decorative background full"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />
        <Container maxWidth={false} sx={{position: 'relative', zIndex: 1}}>
          <Box sx={{ textAlign: 'center' }}>
            {/* --- CHANGE: Heading now comes from left --- */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fromLeftVariant}>
              <Typography sx={{ fontFamily: 'Manrope', color: '#0064FF', fontWeight: 600, mb: 2 , fontSize:'18px'}}>
                WHY CHOOSE US
              </Typography>
              <Typography  sx={{ fontFamily: 'Manrope', fontWeight: 500, color: '#999999', mb: 4 , fontSize:'44px'}}>
                Designed for <Typography component="span" variant="inherit" color="#333333">solving</Typography> your <br/> 
                most pressing <Typography component="span" variant="inherit" color="#333333">cloud efficiency </Typography>and <Typography component="span" variant="inherit" color="#333333">security challenges</Typography>
              </Typography>

            </motion.div>

            {/* --- CHANGE: Buttons now come from right --- */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fromRightVariant}>
              <Box sx={{ display: 'inline-flex', borderRadius: '30px', mb: 5 , backgroundColor:'#F3F3F3'}}>
                <Button onClick={() => setActiveTab('with')} sx={{ textTransform: 'none', fontFamily: 'Manrope', fontWeight: 500, borderRadius: '30px', px: '35px', py: '10px', backgroundColor: activeTab === 'with' ? '#0064FF' : 'transparent', color: activeTab === 'with' ? '#FFFFFF' : '#999999', '&:hover': { backgroundColor: activeTab === 'with' ? '#0053D9' : '#F0F0F0' } }}>
                  With Horizon
                </Button>
                <Button onClick={() => setActiveTab('without')} sx={{ textTransform: 'none', fontFamily: 'Manrope', fontWeight: 500, borderRadius: '30px',  px: '35px', py: '10px', backgroundColor: activeTab === 'without' ? '#0064FF' : 'transparent', color: activeTab === 'without' ? '#FFFFFF' : '#999999', '&:hover': { backgroundColor: activeTab === 'without' ? '#0053D9' : '#F0F0F0' } }}>
                  Without Horizon
                </Button>
              </Box>
            </motion.div>
            
            <Typography sx={{ fontFamily: 'Manrope', color: '#333333', mb: 4, mx: 'auto', fontSize:'24px', fontWeight: 700 }}>
                  Unified, intelligent platform empowers your team with proactive control and efficiency. <br/> See the clear advantage over traditional, fragmented approaches.
                </Typography>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Box sx={{ maxWidth: '600px', mx: 'auto', textAlign: 'left' }}>
                  {(activeTab === 'with' ? withViewData : withoutViewData).map((text, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Box component="img" src={activeTab === 'with' ? tickIconUrl : crossIconUrl} alt={activeTab === 'with' ? 'tick' : 'cross'} sx={{ width: '24px', height: '24px', mr: 2, mt: '2px' }} />
                      <Typography sx={{ fontFamily: 'Manrope', fontWeight:400, fontSize:'18px', color: '#666666' }}>
                        {text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </motion.div>
            </AnimatePresence>
          </Box>
        </Container>
      </Box>

      {/* ======================================================================= */}
      {/* FOOTER SECTION (UNCHANGED)                                              */}
      {/* ======================================================================= */}
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fromBottomVariant}>
        <Box sx={{ backgroundColor: '#0064FF', color: 'white', position: 'relative', overflow: 'hidden' , px:5 }}>
            <Box sx={{ position: 'absolute', top: 0, bottom: 70, right: 0, width: { xs: '90%', md: '70%' }, zIndex: 0, pointerEvents: 'none', pt:2 }}>
              <Box component="img" src={footerBgUrl} alt="background graph" sx={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'bottom right' }}/>
            </Box>
            <Container maxWidth={false} sx={{ position: 'relative', zIndex: 1, py: 8 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                <Box sx={{ width: { xs: '100%', md: '50%' }, textAlign: { xs: 'center', md: 'left' }, mb: { xs: 5, md: 0 } }}>
                  <Typography sx={{ fontFamily: 'Manrope', fontWeight: 600, fontSize: '18px', mb: 2 , color:'#FFFFFF'}}>
                    GET STARTED!
                  </Typography>
                  <Typography sx={{ fontFamily: 'Manrope', fontWeight: 500, fontSize: '44px', mb: 2, color:'#FFFFFF' }}>
                    Start tracking<br/>your cloud costs today.
                  </Typography>
                  <Typography sx={{ fontFamily: 'Manrope', mb: 4, fontWeight:400, color:'#E6E6E6', maxWidth: '450px', mx: { xs: 'auto', md: 0 } }}>
                    Gain instant visibility, set smarter budgets, and discover where your money really goes.
                  </Typography>
                  <Button variant="contained" onClick={() => navigate("/login")} sx={{ backgroundColor: '#FFFFFF', color: '#0064FF', fontFamily: 'Manrope', fontWeight: 600, fontSize:'16px', textTransform: 'none', py: 1.5, px: 8.5, borderRadius: '12px', '&:hover': { backgroundColor: '#f0f0f0' } }}>
                    Login
                  </Button>
                </Box>
              </Box>
            </Container>
            <Box sx={{ 
                borderTop: '1px solid transparent',
                borderImage: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.4), rgba(255,255,255,0)) 1',
              }}>
              <Container maxWidth={false}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
                  <Typography sx={{ fontFamily: 'Manrope', fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>
                    © 2025 Searce. All rights reserved.
                  </Typography>
                </Box>
              </Container>
            </Box>
        </Box>
      </motion.div>
    </>
  );
};

// You might need a RotatingText component if it's in a separate file.
// For this example, I'll assume it exists. If not, here is a placeholder:

// You might need a useNavigate component if it's in a separate file.
// For this example, I'll assume it exists. If not, here is a placeholder:


export default LandingPage;



